import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CheckCircle2, ChevronRight, Filter, Inbox, Mail, RefreshCw, XCircle } from 'lucide-react';
import type { DayRow as DayFigures } from '@nlinks/shared';
import { useKeywordDashboard, useMailAccountMutations, useMailAccounts } from '@/lib/queries';
import { formatCompact, formatNumber, formatRelative } from '@/lib/format';
import { StatCard } from '@/components/StatCard';
import { InfoButton } from '@/components/InfoButton';
import { AdSlot } from '@/components/AdSlot';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Select,
  humanizeStatus,
  statusTone,
  useToast,
} from '@/components/ui';

const COLORS = { matched: '#a78bfa', success: '#34d399', failed: '#f87171' };

/**
 * What each figure counts.
 *
 * Everything after "matched" is deliberately a subset of the matched mail —
 * unmatched mail is never opened, so it has no links, no visits and no
 * outcomes to report. Keeping these definitions in one place stops the tiles,
 * the table header and the tooltips from drifting apart.
 */
const KPI = {
  arrived: 'Every message that landed in your mailboxes, whether or not it matched your keywords.',
  matched: 'Messages matching your keywords. Only these are stored and opened — everything else is counted here and discarded.',
  withLinks: 'Matched messages that contained at least one visitable link. Unsubscribe links and tracking pixels are never counted.',
  linksFound: 'Total links found inside matched messages. One message often contains several.',
  opened:
    'Matched mail with at least one link that was reached. Counted once per mail, however many links it held — a newsletter with twenty links is one opened mail. A destination whose certificate has expired still counts as reached, since the link in the mail resolved.',
  failed:
    'Matched mail that had links, none of which could be reached — dead addresses, timeouts, or pages returning an error.',
} as const;

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-surface-border bg-surface-overlay px-3 py-2 shadow-xl">
      <p className="mb-1.5 text-xs font-medium text-slate-300">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="flex items-center gap-2 text-xs text-slate-400">
          <span className="h-2 w-2 rounded-full" style={{ background: entry.color }} />
          {entry.name}: <span className="font-medium tabular-nums text-slate-200">{formatNumber(entry.value)}</span>
        </p>
      ))}
    </div>
  );
}

function DayRow({ row }: { row: DayFigures }) {
  return (
    <tr className="transition-colors hover:bg-surface-raised/50">
      <td className="whitespace-nowrap font-medium">
        {/*
          The day is the way into the detail view, so it is a real link rather
          than a click handler on the row: it can be opened in a new tab,
          reached by keyboard, and shows its destination on hover.
          `row.day` is already the UTC YYYY-MM-DD the API buckets on, so it is
          passed through untouched — reformatting it locally would send the
          viewer's timezone to a server that thinks in UTC.
        */}
        <Link
          to={`/days/${row.day}`}
          className="inline-flex items-center gap-1.5 text-slate-200 transition hover:text-brand-300"
        >
          {new Date(`${row.day}T00:00:00.000Z`).toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          })}
          <ChevronRight className="h-3.5 w-3.5 opacity-50" aria-hidden />
        </Link>
      </td>
      <td className="text-right tabular-nums text-slate-400">{formatNumber(row.messagesSeen)}</td>
      <td className="text-right tabular-nums text-brand-300">{formatNumber(row.messagesMatched)}</td>
      <td className="text-right tabular-nums text-slate-300">{formatNumber(row.messagesWithLinks)}</td>
      <td className="text-right tabular-nums text-slate-300">{formatNumber(row.linksFound)}</td>
      <td className="text-right tabular-nums text-emerald-400">{formatNumber(row.mailsOpened)}</td>
      <td className="text-right tabular-nums text-red-400">{formatNumber(row.mailsNotOpened)}</td>
    </tr>
  );
}

/** Table header cell carrying its own explanation. */
function Th({ label, info }: { label: string; info: string }) {
  return (
    <th className="text-right">
      <span className="inline-flex items-center justify-end gap-1.5">
        {label}
        <InfoButton label={label} align="right">
          {info}
        </InfoButton>
      </span>
    </th>
  );
}

export function DashboardPage() {
  const [selectedBatch, setSelectedBatch] = useState<string | undefined>(undefined);
  const { data, isLoading } = useKeywordDashboard(selectedBatch);
  const { data: accounts = [] } = useMailAccounts();
  const { syncAll } = useMailAccountMutations();
  const { push } = useToast();

  const today = data?.today;
  const batches = data?.batches ?? [];
  const activeBatches = batches.filter((b) => b.isActive);
  const selected = batches.find((b) => b.id === data?.selectedBatchId) ?? null;

  const handleSyncAll = async () => {
    try {
      const result = await syncAll.mutateAsync();
      push('success', result.queued > 0 ? `Checking ${result.queued} mailbox(es) now.` : 'No mailboxes are active.');
    } catch {
      push('error', 'Could not start a check.');
    }
  };

  // Oldest-first for the chart; the table below reads newest-first.
  const chartData = [...(data?.days ?? [])]
    .reverse()
    .slice(-30)
    .map((d) => ({
      ...d,
      label: new Date(d.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    }));

  // Longest bar in the group; everything else is drawn relative to it.
  const busiestToday = accounts.reduce((max, a) => Math.max(max, a.stats.messagesToday), 0);

  const failing = accounts.filter((a) => a.syncStatus === 'AUTH_FAILED' || a.syncStatus === 'CONNECTION_ERROR');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Your matched mail, and what happened to the links inside it.</p>
        </div>
        <div className="flex items-center gap-2">
          {batches.length > 0 ? (
            <Select
              value={selectedBatch ?? ''}
              onChange={(e) => setSelectedBatch(e.target.value || undefined)}
              className="w-auto min-w-[13rem] text-xs"
            >
              <option value="">All keyword batches</option>
              {batches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.isActive ? '● ' : ''}
                  {batch.label}
                </option>
              ))}
            </Select>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSyncAll}
            loading={syncAll.isPending}
            icon={<RefreshCw className="h-3.5 w-3.5" />}
          >
            Check now
          </Button>
        </div>
      </div>

      {accounts.length === 0 ? (
        <Alert
          severity="info"
          action={
            <Link to="/mailboxes">
              <Button size="sm">Add mailbox</Button>
            </Link>
          }
        >
          Add a mailbox to start monitoring.
        </Alert>
      ) : null}

      {activeBatches.length === 0 && accounts.length > 0 ? (
        <Alert
          severity="error"
          title="Monitoring is paused — no keywords set"
          action={
            <Link to="/keywords">
              <Button size="sm" variant="secondary">
                Add keywords
              </Button>
            </Link>
          }
        >
          Your mailboxes are <strong className="font-semibold text-slate-200">not being checked</strong>. Nothing is
          fetched and no links are opened until at least one keyword batch is active — the app will not open mail
          you have not asked it to. Add a batch and monitoring starts immediately.
        </Alert>
      ) : null}

      {failing.map((account) => (
        <Alert
          key={account.id}
          severity="error"
          action={
            <Link to="/mailboxes">
              <Button size="sm" variant="secondary">
                Fix
              </Button>
            </Link>
          }
        >
          {account.label}: {account.lastError ?? 'sync is failing'}
        </Alert>
      ))}

      {/* Today */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-slate-300">Today</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Mails arrived"
            value={formatNumber(today?.messagesSeen ?? 0)}
            icon={<Mail className="h-4 w-4" />}
            info={KPI.arrived}
            loading={isLoading}
          />
          <StatCard
            label="Matched your keywords"
            value={formatNumber(today?.messagesMatched ?? 0)}
            icon={<Filter className="h-4 w-4" />}
            tone="brand"
            info={KPI.matched}
            loading={isLoading}
          />
          <StatCard
            label="Mails opened"
            value={formatNumber(today?.mailsOpened ?? 0)}
            icon={<CheckCircle2 className="h-4 w-4" />}
            tone="success"
            info={KPI.opened}
            loading={isLoading}
          />
          <StatCard
            label="Mails not opened"
            value={formatNumber(today?.mailsNotOpened ?? 0)}
            icon={<XCircle className="h-4 w-4" />}
            tone={today && today.mailsNotOpened > 0 ? 'danger' : 'default'}
            info={KPI.failed}
            loading={isLoading}
          />
        </div>
      </div>

      {/* Running totals for the selected keyword set */}
      {selected ? (
        <Card>
          <CardHeader
            title={`Batch: ${selected.label}`}
            description={
              <span className="break-anywhere font-mono text-xs">
                {selected.raw.slice(0, 160)}
                {selected.raw.length > 160 ? '…' : ''}
              </span>
            }
          />
          <div className="grid gap-4 p-5 sm:grid-cols-3 xl:grid-cols-5">
            {[
              { label: 'Matched', value: selected.totals.messagesMatched, tone: 'text-brand-300', info: KPI.matched },
              { label: 'With links', value: selected.totals.messagesWithLinks, tone: 'text-slate-100', info: KPI.withLinks },
              { label: 'Links found', value: selected.totals.linksFound, tone: 'text-slate-100', info: KPI.linksFound },
              { label: 'Mails opened', value: selected.totals.mailsOpened, tone: 'text-emerald-300', info: KPI.opened },
              { label: 'Not opened', value: selected.totals.mailsNotOpened, tone: 'text-red-300', info: KPI.failed },
            ].map((item) => (
              <div key={item.label}>
                <p className="flex items-center gap-1.5 text-xs text-slate-500">
                  {item.label}
                  <InfoButton label={item.label}>{item.info}</InfoButton>
                </p>
                <p className={`mt-1 text-2xl font-semibold tabular-nums ${item.tone}`}>
                  {formatCompact(item.value)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <AdSlot slot={import.meta.env.VITE_ADSENSE_SLOT_DASHBOARD} format="leaderboard" />

      {chartData.length > 0 ? (
        <Card>
          <CardHeader title="Daily activity" description={selected ? `Batch: ${selected.label}` : "Across every keyword batch"} />
          <div className="h-72 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  {Object.entries(COLORS).map(([key, color]) => (
                    <linearGradient key={key} id={`g-${key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="label" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="messagesMatched" name="Matched" stroke={COLORS.matched} fill="url(#g-matched)" strokeWidth={2} />
                <Area type="monotone" dataKey="mailsOpened" name="Mails opened" stroke={COLORS.success} fill="url(#g-success)" strokeWidth={2} />
                <Area type="monotone" dataKey="mailsNotOpened" name="Not opened" stroke={COLORS.failed} fill="url(#g-failed)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      ) : null}

      {/*
        Today, per mailbox — the dashboard above is the whole account, and this
        is the same day broken down by where the mail landed. Bars are relative
        to the busiest mailbox, so the comparison is readable without needing an
        axis.
      */}
      {accounts.length > 0 ? (
        <Card>
          <CardHeader
            title="Mail received today, by mailbox"
            description="UTC day, matching the figures above"
            action={
              <Link to="/mailboxes">
                <Button size="sm" variant="secondary">
                  Manage
                </Button>
              </Link>
            }
          />
          <ul className="divide-y divide-surface-border">
            {accounts.map((account) => {
              const count = account.stats.messagesToday;
              const width = busiestToday > 0 ? Math.max((count / busiestToday) * 100, count > 0 ? 4 : 0) : 0;
              return (
                <li key={account.id} className="px-5 py-3.5">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-200">{account.label}</p>
                      <p className="truncate text-xs text-slate-500">{account.emailAddress}</p>
                    </div>
                    <span className="tabular-nums text-sm font-semibold text-slate-100">
                      {formatNumber(count)}
                    </span>
                    <Badge tone={account.syncEnabled ? statusTone(account.syncStatus) : 'neutral'}>
                      {account.syncEnabled ? humanizeStatus(account.syncStatus) : 'Paused'}
                    </Badge>
                    <span className="w-28 text-right text-xs text-slate-600">
                      {formatRelative(account.lastSyncAt)}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-base">
                    <div
                      className="h-full rounded-full bg-brand-400/70 transition-all"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : null}

      <Card>
        <CardHeader
            title="Day by day"
            description={
              selected
                ? `Matched figures for ${selected.label} — open a day to see its mail`
                : 'Every batch combined — open a day to see its mail'
            }
          />
        {isLoading ? (
          <div className="p-4">
            <div className="skeleton h-40 w-full" />
          </div>
        ) : data && data.days.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Day</th>
                  <Th label="Arrived" info={KPI.arrived} />
                  <Th label="Matched" info={KPI.matched} />
                  <Th label="With links" info={KPI.withLinks} />
                  <Th label="Links found" info={KPI.linksFound} />
                  <Th label="Mails opened" info={KPI.opened} />
                  <Th label="Not opened" info={KPI.failed} />
                </tr>
              </thead>
              <tbody>
                {data.days.map((row) => (
                  <DayRow key={row.day} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<Inbox className="h-6 w-6" />}
            title="Nothing yet"
            description={
              accounts.length === 0
                ? 'Add a mailbox and results appear here within a few minutes.'
                : 'Your mailboxes are connected. Results appear after the first check.'
            }
          />
        )}
      </Card>
    </div>
  );
}
